/**
 * Admin → Pal Audit.
 *
 * Review queue for pals whose stats can't be legitimate: level 81+, any IV
 * over 100, or more than 80 soul upgrades in total. Nothing is auto-banned —
 * every row here waits for a human. Ban issues the full treatment (in-game,
 * banlist.txt, website, Discord notice); Dismiss marks it a false positive.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Ban, Check, Play, RefreshCw, Settings2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";

const STATUSES = [
  ["open", "Needs review"],
  ["banned", "Banned"],
  ["cleared", "Dismissed"],
  ["all", "Everything"],
];

export default function PalAudit() {
  const [cfg, setCfg] = useState(null);
  const [servers, setServers] = useState([]);
  const [flags, setFlags] = useState([]);
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showCfg, setShowCfg] = useState(false);
  const toast = useToast();

  const load = useCallback(async (nextStatus = status) => {
    try {
      const [c, f, r] = await Promise.all([
        api.get("/admin/pal-audit/config"),
        api.get(`/admin/pal-audit/flags?status=${nextStatus}`),
        api.get("/admin/pal-audit/runs?limit=10"),
      ]);
      setCfg(c.data.config);
      setServers(c.data.servers || []);
      setFlags(f.data.flags || []);
      setRuns(r.data.runs || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, toast]);

  useEffect(() => { load(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCfg = async (patch) => {
    try {
      const { data } = await api.put("/admin/pal-audit/config", patch);
      setCfg(data.config);
      toast.success("Audit settings saved");
      load(status);
    } catch (e) { toast.error(apiError(e)); }
  };

  const scan = async (server) => {
    setBusy(`scan:${server}`);
    try {
      await api.post(`/admin/pal-audit/scan?server=${encodeURIComponent(server)}`);
      toast.success(`Scan started for ${server} — this takes a few minutes`);
      setTimeout(() => load(status), 4000);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const act = async (id, kind) => {
    setBusy(`${kind}:${id}`);
    try {
      await api.post(`/admin/pal-audit/flags/${id}/${kind}`, {});
      toast.success(kind === "ban" ? "Player banned everywhere" : "Flag dismissed");
      load(status);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  if (loading) return <Loader label="Loading audit queue…" />;

  return (
    <div className="space-y-4 fade-up" data-testid="pal-audit-panel">
      <div className="glass-card p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <AlertTriangle size={16} className="text-[#e5a53f]" />
          <h3 className="text-lg">Pal audit</h3>
          <span className="text-xs text-[var(--muted)]" data-testid="pal-audit-thresholds">
            Flags level {cfg?.level_flag_at}+ · IV over {cfg?.iv_max} · souls over {cfg?.souls_total_max} total
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowCfg((s) => !s)}
              className="btn btn-secondary text-xs flex items-center gap-1.5"
              data-testid="pal-audit-settings-toggle"
            >
              <Settings2 size={13} /> Settings
            </button>
            <button
              onClick={() => load(status)}
              className="btn btn-secondary text-xs flex items-center gap-1.5"
              data-testid="pal-audit-refresh"
            >
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>

        {showCfg && cfg && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pal-audit-settings">
            <NumberField label="Flag level at" value={cfg.level_flag_at}
              onSave={(v) => saveCfg({ level_flag_at: v })} testid="pal-audit-level" />
            <NumberField label="Max legal IV" value={cfg.iv_max}
              onSave={(v) => saveCfg({ iv_max: v })} testid="pal-audit-iv" />
            <NumberField label="Max souls total" value={cfg.souls_total_max}
              onSave={(v) => saveCfg({ souls_total_max: v })} testid="pal-audit-souls" />
            <NumberField label="Scan every (hours)" value={cfg.interval_hours}
              onSave={(v) => saveCfg({ interval_hours: v })} testid="pal-audit-interval" />
            <label className="glass-card p-3 flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg.enabled}
                onChange={(e) => saveCfg({ enabled: e.target.checked })}
                data-testid="pal-audit-enabled"
              />
              Scheduled scans on
            </label>
          </div>
        )}

        <div className="mt-4 divide-y divide-white/5">
          {servers.map((s) => (
            <div key={s.server} className="py-3 flex items-center gap-3 flex-wrap"
                 data-testid={`pal-audit-server-${s.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{s.server}</div>
                <div className="text-[11px] text-[var(--muted)]">
                  {s.modded ? "modded" : "vanilla"} · {s.scan ? "scanned" : "not scanned"}
                  {s.modded && !s.scan && " (Builder sells past vanilla caps here)"}
                </div>
              </div>
              <label className="text-xs flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={!!s.scan}
                  onChange={(e) => saveCfg({ servers: { ...(cfg.servers || {}), [s.server]: e.target.checked } })}
                  data-testid={`pal-audit-scan-toggle-${s.slug}`}
                />
                scan
              </label>
              <button
                onClick={() => scan(s.server)}
                disabled={busy === `scan:${s.server}`}
                className="btn btn-secondary text-xs flex items-center gap-1.5"
                data-testid={`pal-audit-scan-now-${s.slug}`}
              >
                <Play size={12} /> {busy === `scan:${s.server}` ? "Starting…" : "Scan now"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card p-5">
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <h3 className="text-lg">Flagged pals</h3>
          <div className="ml-auto flex gap-2">
            {STATUSES.map(([k, label]) => (
              <button
                key={k}
                onClick={() => setStatus(k)}
                className={`btn text-xs ${status === k ? "" : "btn-secondary"}`}
                data-testid={`pal-audit-filter-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {flags.length === 0 ? (
          <p className="text-sm text-[var(--muted)] py-6 text-center" data-testid="pal-audit-empty">
            Nothing here. {status === "open" ? "No cheated pals awaiting review." : "No records."}
          </p>
        ) : (
          <div className="divide-y divide-white/5" data-testid="pal-audit-rows">
            {flags.map((f) => (
              <div key={f.id} className="py-3 flex items-start gap-3 flex-wrap"
                   data-testid={`pal-audit-flag-${f.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <span className="font-semibold">{f.player_name || f.player_uid}</span>
                    <span className="text-[var(--muted)]"> · {f.pal_name} </span>
                    <span className="glow-gold">Lv {f.level}</span>
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-0.5">
                    {f.reasons.join(" · ")}
                  </div>
                  <div className="text-[11px] text-[var(--muted)] font-mono mt-0.5 truncate">
                    {f.server} · IVs {Object.values(f.ivs || {}).join("/")} · souls {f.souls_total} · {f.instance_id}
                  </div>
                </div>
                {f.status === "open" ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => act(f.id, "ban")}
                      disabled={busy === `ban:${f.id}`}
                      className="btn text-xs flex items-center gap-1.5"
                      data-testid={`pal-audit-ban-${f.id}`}
                    >
                      <Ban size={12} /> Ban
                    </button>
                    <button
                      onClick={() => act(f.id, "dismiss")}
                      disabled={busy === `dismiss:${f.id}`}
                      className="btn btn-secondary text-xs flex items-center gap-1.5"
                      data-testid={`pal-audit-dismiss-${f.id}`}
                    >
                      <Check size={12} /> Dismiss
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-[var(--muted)] uppercase tracking-wider">
                    {f.status} {f.reviewed_by ? `· ${f.reviewed_by}` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass-card p-5">
        <h3 className="text-lg mb-3">Recent scans</h3>
        {runs.length === 0 ? (
          <p className="text-sm text-[var(--muted)]" data-testid="pal-audit-runs-empty">
            No scans yet — hit "Scan now" above.
          </p>
        ) : (
          <div className="divide-y divide-white/5" data-testid="pal-audit-runs">
            {runs.map((r) => (
              <div key={r.id} className="py-2.5 text-xs flex items-center gap-3 flex-wrap"
                   data-testid={`pal-audit-run-${r.id}`}>
                <span className="font-mono text-[var(--muted)]">#{r.id}</span>
                <span className="flex-1 min-w-0 truncate">{r.server}</span>
                <span className={r.status === "error" ? "text-[var(--error)]" : "text-[var(--muted)]"}>
                  {r.status}
                </span>
                <span className="text-[var(--muted)]">
                  {r.pals_scanned} pals / {r.players_scanned} players · {r.flagged} flagged
                </span>
                <span className="text-[var(--muted)] hidden sm:inline">{r.triggered_by}</span>
                {r.error && <span className="text-[var(--error)] w-full truncate">{r.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NumberField({ label, value, onSave, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <div className="glass-card p-3">
      <div className="label">{label}</div>
      <div className="flex gap-2 mt-1">
        <input
          type="number"
          value={v}
          onChange={(e) => setV(e.target.value)}
          className="input flex-1"
          data-testid={testid}
        />
        <button
          onClick={() => onSave(parseInt(v, 10))}
          disabled={String(v) === String(value)}
          className="btn btn-secondary text-xs"
          data-testid={`${testid}-save`}
        >
          Save
        </button>
      </div>
    </div>
  );
}
